import React, { useCallback, useEffect, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	WorkCategoryFeature,
	Feature,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/types';
import { DetailsList } from '../../../../../../common/ui/details-list/index.tsx';
import { FeatureToggleTile } from '../../../../../../common/ui/feature-toggle-tile/index.tsx';
import useAffectedRequestTypes from '../../../../../../controllers/affected-request-types';
import { FEATURE_CONFIG } from './constants';
import FeatureDescription from './feature-description';
import messages from './messages';

export type TileProps = {
	feature: WorkCategoryFeature;
	saving: boolean;
	isEnabled: boolean;
	onEnable: (feature: Feature) => void;
	onDisable: (feature: Feature, numAffectedRequestTypes?: number, error?: Error) => void;
	focusToggle?: boolean;
	illustration?: React.ReactNode;
	// TODO: remove optional while clean up `enable_jsm_repackaging_changes_vtwk9`
	hasPaymentError?: boolean;
};

const Tile = ({
	feature,
	saving,
	isEnabled,
	onEnable,
	onDisable,
	focusToggle,
	hasPaymentError,
}: TileProps) => {
	const {
		illustration,
		title,
		description,
		learnMoreLink,
		detailsTitle,
		details,
		automationSystemRules,
		inProductHelpArticleId,
		descriptionForIPH,
		learnMoreLinkText,
		learnMoreLinkClickEventName,
		popperText,
		isPremiumFeature,
	} = FEATURE_CONFIG[feature];

	const { formatMessage } = useIntl();
	const [isStartingDisable, setStartingDisable] = useState(false);
	const {
		data: { numAffectedRequestTypes },
		error: affectedRequestTypesError,
		fetch: fetchRequestTypes,
	} = useAffectedRequestTypes(feature);

	useEffect(() => {
		if (ff('enable_jsm_repackaging_changes_vtwk9')) {
			if (
				isStartingDisable &&
				(numAffectedRequestTypes !== undefined || affectedRequestTypesError)
			) {
				setStartingDisable(false);
				onDisable(feature, numAffectedRequestTypes, affectedRequestTypesError);
			}
		} else {
			// eslint-disable-next-line no-lonely-if
			if (numAffectedRequestTypes !== undefined || affectedRequestTypesError) {
				setStartingDisable(false);
				onDisable(feature, numAffectedRequestTypes, affectedRequestTypesError);
			}
		}
	}, [affectedRequestTypesError, feature, numAffectedRequestTypes, isStartingDisable, onDisable]);

	const disable = useCallback(() => {
		setStartingDisable(true);
		fetchRequestTypes();
	}, [setStartingDisable, fetchRequestTypes]);

	const handleOnChange = (
		selectedFeature: Feature,
		enabled: boolean,
		analyticEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticEvent, 'settingsToggle', { field: feature });
		if (enabled) {
			onEnable(selectedFeature);
		} else {
			disable();
		}
	};
	return (
		<FeatureToggleTile
			key={feature}
			feature={feature}
			title={formatMessage(title)}
			value={isEnabled}
			saving={saving || isStartingDisable}
			onChange={handleOnChange}
			focusToggle={focusToggle}
			illustration={illustration}
			{...(ff('enable_jsm_repackaging_changes_vtwk9')
				? {
						tooltip: popperText,
						isPopperOpenByDefault: hasPaymentError,
						disabled: hasPaymentError,
					}
				: {})}
			{...(ff('enable_jsm_repackaging_changes_vtwk9') && isPremiumFeature
				? {
						badge: <Lozenge appearance="new">{formatMessage(messages.premiumLozenge)}</Lozenge>,
					}
				: {})}
		>
			<FeatureDescription
				description={description}
				learnMoreLink={learnMoreLink}
				inProductHelpArticleId={inProductHelpArticleId}
				descriptionForIPH={descriptionForIPH}
				learnMoreLinkText={learnMoreLinkText}
				learnMoreLinkClickEventName={learnMoreLinkClickEventName}
			/>
			{details && detailsTitle && (
				<DetailsList
					title={formatMessage(detailsTitle)}
					messages={details}
					automationSystemRules={automationSystemRules}
				/>
			)}
		</FeatureToggleTile>
	);
};

export default Tile;
